<script setup name="TvbVideoDetailItem">
import { inject, computed, watch, ref } from 'vue'
import dayjs from 'dayjs'

const props = defineProps({
  info: {
    type: Object,
    default: () => {}
  }
})

const size = inject('size')
const setCurrentVideo = inject('setCurrentVideo')
const currentVideo = inject('currentVideo')
const isPlaying = computed(() => currentVideo.value.id === props.info.id)
const emits = defineEmits(['scroll-to'])
const listItemsRef = ref()

// 计算滚动目标：当前播放项定位到第二个卡片位（上方保留一张卡片高度）
const getSecondSlotScrollTop = () => {
  const el = listItemsRef.value
  if (!el) return 0
  // 当前卡片顶部位置减去自身高度 ≈ 上一张卡片的顶部位置，滚动至该处后当前项位于可视区第二行
  const secondSlotTop = el.offsetTop - el.offsetHeight
  if (secondSlotTop <= 0) return 0
  // 再减去卡片上下 padding，避免顶部露出上一张卡片的多余间隙
  return secondSlotTop - (['s', 'xs'].includes(size.value) ? 6 : 8)
}

watch(isPlaying, (isPlaying) => {
  if (isPlaying) {
    emits('scroll-to', getSecondSlotScrollTop())
  }
})

const handleClick = () => {
  if (isPlaying.value) return
  setCurrentVideo(props.info)
}
</script>

<template>
  <div
    class="tvb-video-detail_item"
    :class="{ 'is-playing': isPlaying, [size]: size }"
    @click="handleClick"
    ref="listItemsRef"
  >
    <div class="tvb-video-detail_item-image">
      <div class="tvb-video-detail_item-play">
        <tvb-svg-icon class="tvb-video-detail_item-play-icon" name="icon_shorts_play" />
      </div>
      <div class="tvb-video-detail_item-playing">
        <ul class="tvb-video-detail_item-playing-loader">
          <li v-for="item in 3" :key="item"></li>
        </ul>
        <p class="tvb-video-detail_item-playing-text">當前播放</p>
      </div>
      <tvb-image
        class="tvb-video-detail_item-image-el"
        :src="['s', 'xs'].includes(size) ? info?.image_info?.small : info?.image_info?.medium"
        :alt="info.title"
        transform
      />
    </div>
    <div class="tvb-video-detail_item-info">
      <h4 class="tvb-video-detail_item-title">
        <span>{{ info.title }}</span>
      </h4>
      <div class="tvb-video-detail_item-other">
        <p class="tvb-video-detail_item-category">{{ info.category_name }}</p>
        <p class="tvb-video-detail_item-updated_at">
          {{ dayjs.unix(info.updated_at).format('YYYY-MM-DD HH:mm') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin keyframes($name) {
  @keyframes #{$name} {
    @content;
  }
}

@mixin animation($str) {
  animation: #{$str};
}

.tvb-video-detail_item {
  padding: 8px;
  display: flex;
  gap: 10px;
  border-radius: 20px;
  margin: 0 8px;
  cursor: pointer;
  transition: all 0.15s ease-in;

  &:last-child {
    // 设计稿列表底部间距 pb 24
    margin-bottom: 24px;
  }

  &:first-child {
    // 对齐设计稿 list pt 8，消除原 14px 多出的顶部间距
    margin-top: 8px;
  }

  // hover 样式：卡片背景轻微变化，封面播放按钮显示（当前播放项除外）
  &:hover:not(.is-playing) {
    background: rgba(0, 0, 0, 0.04);

    .tvb-video-detail_item-image {
      .tvb-video-detail_item-play {
        opacity: 1;
      }
    }
  }

  &.s,
  &.xs {
    padding: 6px;
    margin: 0 6px;
    // 工单：移动端封面与文字两组件间距为 4px（Figma 移动卡片规格）
    gap: 4px;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 18px;
    }

    &:first-child {
      margin-top: 6px;
    }

    .tvb-video-detail_item-image {
      width: 128px;
      min-width: 128px;
      height: 72px;
      .tvb-video-detail_item-image-el {
        border-radius: 12px;
      }
      .tvb-video-detail_item-playing {
        .tvb-video-detail_item-playing-text {
          font-size: 13px;
        }
      }
    }

    .tvb-video-detail_item-info {
      .tvb-video-detail_item-title {
        line-height: 24px;
      }

      .tvb-video-detail_item-other {
        gap: 10px;
        .tvb-video-detail_item-category,
        .tvb-video-detail_item-updated_at {
          font-size: 13px;
          line-height: 18px;
          white-space: nowrap;
        }
      }
    }
  }

  .tvb-video-detail_item-image {
    width: 138px;
    min-width: 138px;
    height: 78px;
    // 封面圆角按设计稿调整为 12px（移动端同规格）
    border-radius: 12px;
    border: 0.5px solid rgba(163, 178, 188, 0.35);
    position: relative;

    .tvb-video-detail_item-image-el {
      width: 100%;
      height: 100%;
      // 封面圆角按设计稿调整为 12px（移动端同规格）
      border-radius: 12px;
      object-fit: cover;
      position: relative;
      z-index: 1;
    }

    .tvb-video-detail_item-play {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9;
      // 默认隐藏，hover 非播放项时显示（见卡片 hover 规则）
      opacity: 0;
      transition: all 0.15s ease-in;

      .tvb-video-detail_item-play-icon {
        // width: 14px;
        // height: 14px;
        font-size: 18px;
      }
    }

    .tvb-video-detail_item-playing {
      position: absolute;
      left: 8px;
      bottom: 8px;
      display: flex;
      height: 28px;
      padding: 0 8px;
      align-items: center;
      gap: 4px;
      border-radius: 10px;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9;
      opacity: 0;
      transition: all 0.15s ease-in;

      .tvb-video-detail_item-playing-loader {
        height: 12px;
        list-style: none;

        li {
          width: 2px;
          height: 12px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 2px;
          float: right;

          @include keyframes(loadbars) {
            0% {
              height: 5px;
              margin-top: 7px;
            }

            50% {
              height: 12px;
              margin-top: 0px;
            }

            100% {
              height: 7px;
              margin-top: 5px;
            }
          }
        }

        li:first-child {
          @include animation('loadbars .8s cubic-bezier(.645, .045, .355, 1) infinite 0s');
        }

        li:nth-child(2) {
          margin-right: 2px;
          @include animation('loadbars .8s ease-in-out infinite -.4s');
        }

        li:nth-child(3) {
          margin-right: 2px;
          @include animation('loadbars .8s ease-in-out infinite -.6s');
        }
      }

      .tvb-video-detail_item-playing-text {
        color: rgba(255, 255, 255, 0.95);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.3px;
      }
    }
  }

  &.is-playing {
    background: rgba(0, 85, 189, 0.08);

    .tvb-video-detail_item-image {
      .tvb-video-detail_item-play {
        opacity: 0;
      }

      .tvb-video-detail_item-playing {
        opacity: 1;
      }
    }
  }

  .tvb-video-detail_item-info {
    // display: flex;
    // flex-direction: column;
    // justify-content: space-between;
    gap: 6px;
    width: 100%;

    .tvb-video-detail_item-title {
      // 标题颜色按设计稿调整
      color: #1c191a;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 26px;
      letter-spacing: 0.5px;
      // 设计稿卡片标题单行省略
      @include line-text-ellipsis(2);
      span {
        @include text-hover-before();
        &:hover {
          @include text-hover-active();
        }
      }
    }

    .tvb-video-detail_item-other {
      margin-top: auto;
      display: flex;
      gap: 12px;

      .tvb-video-detail_item-category,
      .tvb-video-detail_item-updated_at {
        color: #818d95;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.3px;
      }
      .tvb-video-detail_item-category {
        max-width: calc(100% - 130px);
        @include line-text-ellipsis(1);
      }
    }
  }
}
</style>
